import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Announcement03,
  BarChart10,
  Building05,
  CheckVerified02,
  ChevronDown,
  CursorClick01,
  Home01,
  Settings01,
  Shield02,
  ShieldZap,
  ShoppingBag01,
  UserPlus01,
  Users01,
  VideoRecorder,
} from "@untitledui/icons";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { resolveEnvironmentLabel } from "@/pages/admin/environment-label";
import { TestErrorsModal } from "@/pages/admin/modals/test-errors";
import { useAuth } from "@/providers/auth-provider";
import { cx } from "@/utils/cx";

type HoverMenuProps = {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  align?: "left" | "right";
  children: React.ReactNode;
  theme?: "light" | "dark";
};

const HoverMenu = ({ label, icon: Icon, align = "left", children, theme = "light" }: HoverMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const closeMenu = useCallback(
    (returnFocus = false) => {
      clearCloseTimeout();
      setIsOpen(false);
      if (returnFocus) {
        triggerRef.current?.focus();
      }
    },
    [clearCloseTimeout],
  );

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 50);
  }, [clearCloseTimeout]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeMenu]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (isOpen) {
          closeMenu(true);
        } else {
          openMenu();
          // Focus first focusable element in menu
          requestAnimationFrame(() => {
            const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
              'a, button, [tabindex]:not([tabindex="-1"])',
            );
            firstFocusable?.focus();
          });
        }
        break;
      case "Escape":
        if (isOpen) {
          event.preventDefault();
          closeMenu(true);
        }
        break;
      case "ArrowDown":
        if (!isOpen) {
          event.preventDefault();
          openMenu();
          requestAnimationFrame(() => {
            const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
              'a, button, [tabindex]:not([tabindex="-1"])',
            );
            firstFocusable?.focus();
          });
        }
        break;
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    const focusableElements = menuRef.current?.querySelectorAll<HTMLElement>(
      'a, button, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusableElements?.length) return;

    const focusableArray = Array.from(focusableElements);
    const currentIndex = focusableArray.findIndex((el) => el === document.activeElement);

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        closeMenu(true);
        break;
      case "ArrowDown":
        event.preventDefault();
        if (currentIndex < focusableArray.length - 1) {
          focusableArray[currentIndex + 1]?.focus();
        } else {
          focusableArray[0]?.focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (currentIndex > 0) {
          focusableArray[currentIndex - 1]?.focus();
        } else {
          focusableArray[focusableArray.length - 1]?.focus();
        }
        break;
      case "Tab":
        // Allow tab to close menu and move focus naturally
        closeMenu();
        break;
    }
  };

  return (
    /** biome-ignore lint/a11y/noStaticElementInteractions: wrapper div for hover state, keyboard interaction handled by button */
    <div ref={containerRef} className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        onKeyDown={handleKeyDown}
        onFocus={openMenu}
        onBlur={(e) => {
          // Only close if focus leaves the entire menu container
          if (!containerRef.current?.contains(e.relatedTarget as Node)) {
            closeMenu();
          }
        }}
        className={cx(
          "flex items-center gap-1.5",
          "px-1.5 py-1",
          "text-md font-medium text-fg-white/90",
          "outline-focus-ring transition-colors duration-100 ease-linear",
          "hover:text-fg-white focus:text-fg-white",
        )}
      >
        {Icon && <Icon aria-hidden="true" className="-ml-0.5 size-4 text-fg-white/80" />}
        <span>{label}</span>
        <ChevronDown
          className={cx("size-4 text-fg-white/70 transition-transform duration-150", isOpen && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      <div
        id={menuId}
        ref={menuRef}
        role="menu"
        aria-label={label}
        onKeyDown={handleMenuKeyDown}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        className={cx(
          "absolute top-full z-100 pt-2",
          align === "right" ? "right-0" : "left-0",
          "transition-all duration-150",
          isOpen
            ? "pointer-events-auto visible opacity-100 translate-y-0"
            : "pointer-events-none invisible opacity-0 translate-y-1",
        )}
      >
        <div
          className={cx(
            "min-w-60 overflow-hidden rounded-lg shadow-none ring-1 ring-secondary_alt",
            theme === "light" ? "bg-white" : "bg-[#0C0E12]",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

type MenuLinkProps = {
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  to: string;
  search?: Record<string, unknown>;
  iconColor?: string;
  theme?: "light" | "dark";
};

const MenuLink = ({ label, description, icon: Icon, to, search, iconColor, theme = "light" }: MenuLinkProps) => (
  <Link
    to={to}
    search={search}
    role="menuitem"
    className={cx(
      "block px-1.5 py-px outline-hidden rounded-md",
      `${theme === "light" ? "hover:bg-[#FAFAFA] focus-visible:bg-[#FAFAFA]" : "hover:bg-[#22262F] focus-visible:bg-[#22262F]"} `,
    )}
  >
    <div className={`flex items-start gap-3 rounded-md px-2.5 ${description ? "py-3" : "py-2"} outline-focus-ring`}>
      <Icon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 stroke-[2.25px]"
        style={iconColor ? { color: iconColor } : undefined}
      />
      <div className="min-w-0">
        <div className={cx("text-md font-semibold mb-1", theme === "light" ? "text-[#181D27]" : "text-[#f7f7f7]")}>
          {label}
        </div>
        {description && (
          <div className={`text-sm ${theme === "light" ? "text-[#94979C]" : "text-[#6B7280]"} text-wrap`}>
            {description}
          </div>
        )}
      </div>
    </div>
  </Link>
);

type MenuButtonProps = {
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  onClick: () => void;
  iconColor?: string;
  theme?: "light" | "dark";
};

const MenuButton = ({ label, description, icon: Icon, onClick, iconColor, theme = "light" }: MenuButtonProps) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className={cx(
      "w-full text-left py-px outline-hidden",
      `${theme === "light" ? "hover:bg-[#FAFAFA] focus-visible:bg-[#FAFAFA]" : "hover:bg-[#22262F] focus-visible:bg-[#22262F]"} `,
    )}
  >
    <div className="flex items-start gap-3 rounded-md p-2.5 outline-focus-ring">
      <Icon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 stroke-[2.25px] text-fg-quaternary"
        style={iconColor ? { color: iconColor } : undefined}
      />
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${theme === "light" ? "text-[#181D27]" : "text-[#f7f7f7]"}`}>
          {label}
        </div>
        {description && (
          <div className={`text-xs ${theme === "light" ? "text-[#94979C]" : "text-[#6B7280]"} text-wrap`}>
            {description}
          </div>
        )}
      </div>
    </div>
  </button>
);

export const AdminNavbar = () => {
  const { user } = useAuth();
  const [isTestErrorsOpen, setIsTestErrorsOpen] = useState(false);
  const environment = resolveEnvironmentLabel(import.meta.env.VITE_STAGE);
  const newCollabsGroupsEnabled = useFeatureFlag("F-new-collabs-groups");
  const collabsTargetPath = newCollabsGroupsEnabled ? "/collabs/groups/target" : "/collabs/target";

  if (!user || user.role !== "admin") return null;

  return (
    <>
      <header className="sticky top-0 z-[110]">
        <section className={cx("flex h-12 w-full items-center border-b", environment.bannerClassName)}>
          <div className="flex w-full items-center justify-between px-20">
            <div className="flex flex-1 items-center gap-6">
              <nav aria-label="Admin navigation">
                <ul className="flex items-center gap-6">
                  <li className="flex items-center gap-2 text-md font-semibold text-fg-white/90">
                    <span>{environment.text}</span>
                    <span aria-hidden="true">•</span>
                  </li>
                  <li>
                    <HoverMenu label="Console" theme="dark">
                      <div className="flex w-[640px]">
                        <div className="p-2 flex-1 w-[55%]">
                          <MenuLink
                            label="Customers"
                            description="Manage merchants and workspaces within our system"
                            icon={Building05}
                            to="/admin/customers"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                          <MenuLink
                            label="Users"
                            description="Manage all users with access to our application"
                            icon={UserPlus01}
                            to="/admin/users"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                          <MenuLink
                            label="Ads"
                            description="TikTok Ads auth and GMV Max status for every merchant"
                            icon={CursorClick01}
                            to="/admin/ads"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                          <MenuLink
                            label="Content Safety"
                            description="Monitor AI risk detection and model accuracy across videos"
                            icon={ShieldZap}
                            to="/admin/risk-flags"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                          <MenuLink
                            label="Risk Catalog"
                            description="Add, edit, and configure the risks available across merchants"
                            icon={AlertTriangle}
                            to="/admin/risks"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                          <MenuLink
                            label="In App Updates"
                            description="Product announcements and new page notifications"
                            icon={CheckVerified02}
                            to="/admin/in-app-updates"
                            iconColor="#9E77ED"
                            theme="dark"
                          />
                        </div>
                        <div className="w-[45%] p-4 flex items-start justify-center bg-[#13161B]">
                          <img src="/mockup-admin.png" alt="" aria-hidden="true" className="w-full h-auto rounded-lg" />
                        </div>
                      </div>
                    </HoverMenu>
                  </li>

                  <li>
                    <HoverMenu label="Customer facing App">
                      <div className="flex w-[640px]">
                        <div className="p-2 flex-1 w-[55%]">
                          <MenuLink label="Home" icon={Home01} to="/home" iconColor="#9E77ED" />
                          <MenuLink label="Safety" icon={Shield02} to="/safety-overview" iconColor="#9E77ED" />
                          <MenuLink label="Creators" icon={Users01} to="/creator-explorer" iconColor="#9E77ED" />
                          <MenuLink label="Collabs" icon={Announcement03} to={collabsTargetPath} iconColor="#9E77ED" />
                          <MenuLink
                            label="Analytics"
                            icon={BarChart10}
                            to="/analytics-dashboard"
                            search={{ tab: "overview" }}
                            iconColor="#9E77ED"
                          />
                          <MenuLink label="Videos" icon={VideoRecorder} to="/video-explorer" iconColor="#9E77ED" />
                          <MenuLink label="Products" icon={ShoppingBag01} to="/product-explorer" iconColor="#9E77ED" />
                          <MenuLink
                            label="Settings"
                            icon={Settings01}
                            to="/settings/merchant-details"
                            iconColor="#9E77ED"
                          />
                        </div>
                        <div className="w-[45%] p-4 flex items-center justify-center bg-[#FAFAFA]">
                          <img
                            src="/mockup-customer.png"
                            alt=""
                            aria-hidden="true"
                            className="w-full h-auto rounded-lg"
                          />
                        </div>
                      </div>
                    </HoverMenu>
                  </li>
                </ul>
              </nav>
            </div>

            <div className="ml-auto self-center">
              <HoverMenu label="Actions" icon={CursorClick01} align="right">
                <div className="p-1 w-[248px]">
                  {/* TODO: Implement AI button functionality */}
                  {/* <MenuButton label="Re-run AI model on Video" icon={ZapFast} iconColor="#535862" onClick={() => {}} />
                  <hr className="border-[#E9EAEB]" /> */}
                  <MenuButton
                    label="Test Errors"
                    icon={AlertTriangle}
                    iconColor="#A4A7AE"
                    onClick={() => setIsTestErrorsOpen(true)}
                  />
                </div>
              </HoverMenu>
            </div>
          </div>
        </section>
      </header>

      <TestErrorsModal open={isTestErrorsOpen} onOpenChange={setIsTestErrorsOpen} hideTrigger />
    </>
  );
};
